import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, IconButton, Paper, Typography, Stack } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useEventDetails } from '../context';
import { db } from '../instant';
import ScrollableLayout from 'src/layouts/ScrollableLayout';
import WasteMaterialChart from '../components/WasteMaterialChart';
import { BACKEND_BASE_URL } from 'src/constants';
import { Field, Form, Formik } from 'formik';
import Dropdown, { DropdownOption } from '../components/Dropdown';
import { queryReportTemplates, createReportFormSchema, CreateReportFormValues, ReportTemplate } from '../schema';
import { id } from '@instantdb/react';
import RadioSwitch from '../components/RadioSwitch';
import LoadingIndicator from '../components/LoadingIndicator';
import { CreateReportResponse, createReportResponse, ErrorResponse, ChartLink } from '@zw-app/shared/api';
import { TransactionChunk } from '@instantdb/admin';
import { AppSchema } from '@zw-app/shared/instant.schema';
import { getReportData, REPORT_DERIVED_DATA_PLACEHOLDERS, ReportDataResult } from 'src/reports';
import ItemSelector from '../components/ItemSelector';
import GroupedItemSelector from '../components/GroupedItemSelector';
import { sortMaterialsWithCustomAtBottom } from '../utils';
import { usePermissions } from 'src/hooks/usePermissions';
import PermissionDenied from 'src/components/PermissionDenied';

// Create a mapping from InstantDB IDs to Google Drive IDs
const templateIdToGoogleSlidesId = (templates: ReportTemplate[]): Record<string, string> => {
    return templates.reduce((map: Record<string, string>, template) => {
        map[template.id] = template.googleSlidesId;
        return map;
    }, {});
};

export default function CreateReportScreen() {
    const { canGenerateReports, isLoading: permissionsLoading } = usePermissions();
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();
    const { event, isLoading: dataIsLoading, error: dataError } = useEventDetails(eventId || '');
    const [isExporting, setIsExporting] = useState(false);
    const [getChartBlob, setGetChartBlob] = useState<((format: string) => Promise<Blob>) | null>(null);

    if (!permissionsLoading && !canGenerateReports) {
        return <PermissionDenied requiredPermission="Generate Reports" />;
    }

    const { data: templatesData, isLoading: loadingTemplates, error: templatesError } = queryReportTemplates();
    const templates = templatesData?.reportTemplates || [];

    // Create a map of template IDs to templates for easy lookup
    const templateMap = templates.reduce((map: Record<string, ReportTemplate>, template) => {
        map[template.id] = template;
        return map;
    }, {});

    const { user, isLoading: authIsLoading, error: authError } = db.useAuth();

    // Create a mapping from InstantDB IDs to Google Drive IDs
    const templateIdMap = templateIdToGoogleSlidesId(templates);

    // Add datasets query
    const { data: datasetsData } = db.useQuery({
        wasteDatasets: {
            $: {
                where: { 'event.id': eventId || '' },
                order: { order: 'asc' },
            },
        },
        wasteMaterials: {
            category: {},
            $: { order: { order: 'asc' } },
        },
        wasteCategories: {
            $: { order: { order: 'asc' } },
        },
    });

    const datasets = datasetsData?.wasteDatasets || [];
    const materials = datasetsData?.wasteMaterials || [];
    const categories = datasetsData?.wasteCategories || [];

    const isLoading = dataIsLoading || authIsLoading || loadingTemplates;
    const error = dataError || authError || templatesError;

    if (isLoading) {
        return <LoadingIndicator message="Loading report creation..." />;
    }

    if (error || !eventId) {
        return <Typography color="error">Error loading report data</Typography>;
    }

    const eventName = event?.name || eventId;

    const handleBack = () => {
        navigate(`/event/${eventId}/reports`);
    };

    const generateReport = async (values: CreateReportFormValues) => {
        if (!getChartBlob) {
            alert('Chart is not available for export');
            return;
        }

        // Get the token from the authenticated user
        const token = user?.refresh_token;
        if (!token) {
            alert('You must be logged in to generate reports');
            return;
        }

        setIsExporting(true);

        try {
            // Get the Google Drive ID from the mapping
            const googleSlidesId = templateIdMap[values.templateId];
            if (!googleSlidesId) {
                throw new Error('Invalid template selected');
            }

            const selectedTemplateId = values.templateId;
            const selectedTemplate = templateMap[selectedTemplateId];

            // Format the date for the report with time in user's local timezone
            const now = new Date();
            const formattedDate = now.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });

            // Format date with time for folder naming
            const formattedDateWithTime = now.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
                timeZoneName: 'short',
            });

            // Get chart templates linked to the selected report template
            const chartTemplates = selectedTemplate?.chartTemplates || [];

            // Get report data with the new type-safe structure
            const reportData: ReportDataResult = await getReportData(values.templateId, eventId, values);
            const { chartData, derivedData } = reportData;

            console.log('got report data', { chartData, derivedData });

            // Prepare chart data for the API request
            const charts: ChartLink[] = chartTemplates.map(template => {
                return {
                    templateId: template.googleSheetsId,
                    placeholder: template.placeholder,
                    ranges: [
                        {
                            range: template.dataRange,
                            values: chartData[template.id] || [],
                        },
                    ],
                };
            });

            // Prepare text replacements including derived data
            const textReplacements: Record<string, string> = {
                '{{EVENT_NAME}}': eventName,
                '{{REPORT_DATE}}': formattedDate,
            };

            // Add client name if available
            if (event?.clientName) {
                textReplacements['{{CLIENT_NAME}}'] = event.clientName;
            }

            // Add location if available
            if (event?.location) {
                textReplacements['{{LOCATION}}'] = event.location;
            }

            // Add start date if available (independent of end date)
            if (event?.startDate) {
                const startDate = new Date(event.startDate);
                const formattedStartDate = startDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                });
                textReplacements['{{START_DATE}}'] = formattedStartDate;
            }

            // Add end date if available
            if (event?.endDate) {
                const endDate = new Date(event.endDate);
                const formattedEndDate = endDate.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                });
                textReplacements['{{END_DATE}}'] = formattedEndDate;
            }

            // Initialize image replacements
            const imageReplacements: Record<string, string> = {};

            // Add client logo if available
            if (event?.clientLogoUrl) {
                imageReplacements['{{CLIENT_LOGO}}'] = event.clientLogoUrl;
            }

            // Add derived data to text replacements using shared placeholders
            Object.entries(REPORT_DERIVED_DATA_PLACEHOLDERS).forEach(([key, placeholder]) => {
                // Type-safe access to derived data properties
                const value = (derivedData as any)[key];
                if (value !== undefined) {
                    textReplacements[placeholder] = String(value);
                }
            });

            // Generate the report with the chart data and text replacements
            const createReportBody = {
                texts: textReplacements,
                images: imageReplacements,
                templateId: googleSlidesId,
                charts: charts,
                // Pass both formatted dates
                eventName: eventName,
                templateName: selectedTemplate.name,
                reportDate: formattedDateWithTime, // Use the date with time for folder naming
            };

            console.log('create report body', createReportBody);

            // Call the reports API with the token in the headers
            const reportResponse = await fetch(`${BACKEND_BASE_URL}/create-report`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    token,
                },
                body: JSON.stringify(createReportBody),
            });

            console.log('report response', reportResponse);

            if (!reportResponse.ok) {
                const errorData: ErrorResponse = await reportResponse.json();
                throw new Error(errorData.error || 'Failed to generate report');
            }

            const reportResultData = await reportResponse.json();
            const reportResult: CreateReportResponse = createReportResponse.validateSync(reportResultData);

            // Create a report and chart in InstantDB
            const reportId = id();

            // Prepare transaction with updated schema
            const transaction: TransactionChunk<AppSchema, 'reports' | 'charts' | 'chartsSheets'>[] = [
                // Create the report
                db.tx.reports[reportId]
                    .update({
                        createdAt: new Date().getTime(),
                        materialBreakdown: values.materialBreakdown,
                        reportGoogleSlidesId: reportResult.googleSlidesId,
                        chartsGoogleSheetsId: reportResult.chartsGoogleSheetsId || '',
                    })
                    .link({
                        event: eventId,
                        template: values.templateId,
                        // Link to selected datasets if applicable
                        ...(values.datasetIds?.length ? { datasets: values.datasetIds } : {}),
                        // Link to selected materials if applicable
                        ...(values.materialIds?.length ? { materials: values.materialIds } : {}),
                        // Link to selected categories if applicable
                        ...(values.categoryIds?.length ? { categories: values.categoryIds } : {}),
                    }),
            ];

            // If we have a Google Sheets ID for charts, create a chartsSheet record
            if (reportResult.chartsGoogleSheetsId) {
                const chartSheetId = id();
                const chartSheetUpdate = db.tx.chartsSheets[chartSheetId]
                    .update({
                        name: `${eventName} Charts`,
                        googleSheetsId: reportResult.chartsGoogleSheetsId,
                    })
                    .link({
                        report: reportId,
                    });

                // Link the chartsSheet to the chart templates that were used
                if (chartTemplates.length > 0) {
                    chartSheetUpdate.link({
                        templates: chartTemplates.map(template => template.id),
                    });
                }

                transaction.push(chartSheetUpdate);
            }

            await db.transact(transaction);

            // Navigate to the report info screen
            navigate(`/report/${reportId}`);
        } catch (error) {
            console.error('Error generating report:', error);
            alert(error instanceof Error ? error.message : 'Failed to generate report');
        } finally {
            setIsExporting(false);
        }
    };

    // Convert templates to dropdown options using InstantDB IDs
    const templateOptions: DropdownOption<string>[] = templates.map(template => ({
        label: template.name,
        value: template.id, // Use InstantDB ID as the value
    }));

    // Initial form values
    const initialValues: CreateReportFormValues = {
        templateId: templateOptions.length > 0 ? templateOptions[0].value : '',
        materialBreakdown: true,
        materialIds: [],
        categoryIds: [],
        datasetIds: [],
    };

    // Sort materials - custom materials at the bottom within each category
    const sortedMaterials = sortMaterialsWithCustomAtBottom(materials);

    // Add this before passing materials to GroupedItemSelector
    // Transform materials to include italic property based on custom field
    const materialsWithItalicProp = sortedMaterials.map(material => ({
        ...material,
        italic: material.custom,
    }));

    return (
        <ScrollableLayout>
            {isExporting && <LoadingIndicator message="Generating report..." />}
            <Stack direction="column" spacing={2} sx={{ my: 4, mx: 0, width: '100%', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" mb={3}>
                    <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4">Generate New Report</Typography>
                </Box>

                <Paper
                    sx={{
                        p: { xs: 2, sm: 3 }, // Responsive padding - 16px on mobile, 24px on larger screens
                        mb: 4,
                        minWidth: '300px',
                        width: '100%',
                        maxWidth: '400px',
                        margin: '20px',
                        boxSizing: 'border-box', // Ensure padding is included in width calculation
                    }}
                >
                    <Typography sx={{ ml: { xs: 1, sm: 2 }, mb: 2 }} variant="h6" gutterBottom>
                        {eventName}
                    </Typography>

                    <Formik
                        initialValues={initialValues}
                        validationSchema={createReportFormSchema}
                        onSubmit={generateReport}
                        enableReinitialize
                    >
                        {({ values, isSubmitting, isValid, setFieldValue }) => (
                            <Form>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Field
                                        name="templateId"
                                        component={Dropdown}
                                        options={templateOptions}
                                        label="Report Template"
                                        disabled={isSubmitting || templateOptions.length === 0}
                                        sx={{ minWidth: 250 }}
                                    />

                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                        <Field
                                            name="materialBreakdown"
                                            component={RadioSwitch}
                                            trueLabel="by material (e.g. Cans)"
                                            falseLabel="by category (e.g. Recycling)"
                                            label="Waste breakdown type:"
                                            onChange={async (value: boolean) => {
                                                console.log('materialBreakdown changed', value);
                                                // Reset the relevant field when toggling
                                                if (value) {
                                                    // Switching to material breakdown, reset categoryIds
                                                    await setFieldValue('categoryIds', []);
                                                    console.log('categoryIds reset');
                                                } else {
                                                    // Switching to category breakdown, reset materialIds
                                                    await setFieldValue('materialIds', []);
                                                    console.log('materialIds reset');
                                                }
                                                // setFieldValue('materialBreakdown', value);
                                            }}
                                        />
                                    </Box>

                                    {/* Dataset selector - only show if there are 2+ datasets */}
                                    {datasets.length >= 2 && (
                                        <ItemSelector
                                            label="Datasets to include"
                                            items={datasets}
                                            selectedIds={values.datasetIds || []}
                                            onChange={ids => setFieldValue('datasetIds', ids)}
                                            helperText="Leave empty to include all datasets"
                                            disabled={isSubmitting}
                                        />
                                    )}

                                    {/* Material/Category selector */}
                                    {values.materialBreakdown ? (
                                        <GroupedItemSelector
                                            label="Materials to include"
                                            items={materialsWithItalicProp}
                                            categories={categories}
                                            selectedIds={values.materialIds || []}
                                            onChange={ids => setFieldValue('materialIds', ids)}
                                            helperText="Leave empty to include all materials"
                                            disabled={isSubmitting}
                                        />
                                    ) : (
                                        <ItemSelector
                                            label="Categories to include"
                                            items={categories}
                                            selectedIds={values.categoryIds || []}
                                            onChange={ids => setFieldValue('categoryIds', ids)}
                                            helperText="Leave empty to include all categories"
                                            disabled={isSubmitting}
                                        />
                                    )}

                                    <Typography variant="h5" gutterBottom align="center" sx={{ mt: 3 }}>
                                        Charts
                                    </Typography>
                                    <Paper sx={{ p: 3, mb: 3 }}>
                                        <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                                            <WasteMaterialChart
                                                eventId={eventId}
                                                setGetChartBlob={setGetChartBlob}
                                                materialBreakdown={values.materialBreakdown}
                                                itemIds={values.materialBreakdown ? values.materialIds : values.categoryIds}
                                                datasetIds={values.datasetIds}
                                            />
                                        </Box>
                                    </Paper>

                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={isSubmitting || !isValid || templateOptions.length === 0 || !getChartBlob}
                                            sx={{ minWidth: 200, mt: 2 }}
                                        >
                                            {isSubmitting ? 'Generating...' : 'Generate Report'}
                                        </Button>
                                    </Box>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Paper>
            </Stack>
        </ScrollableLayout>
    );
}
